import React, { useMemo, useState } from 'react';
import * as ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import fileDownload from 'js-file-download';
import moment from 'moment';
import {
  Button, Col, Icons, Row,
} from '../../../components/base/BootstrapComponents/BootstrapComponents';
import TextInputWithLabel from '../../../components/fields/TextInput/TextInputWithLabel';
import { getResponseError } from '../../../utils/utils';
import { dateTimeFormatter, localeFormatter } from '../../../utils/formatters';
import { errorNotification, successNotification } from '../../Notifications/notificationDecorator';
import { generateViolationProtocolPdf } from '../actions';
import ViolationGallery from './ViolationGallery';
import { getViolationStatusDisplayValue } from '../../../configs';

const VEHICLE_TYPE_LABELS = {
  bicycle: 'Велосипед',
  cargo_bicycle: 'Грузовой велосипед',
  scooter: 'Самокат',
  moped: 'Мопед',
  other: 'Другое',
  car: 'Автомобиль',
};

const getCoordinates = location => {
  const coordinates = location?.coordinates;
  if (!Array.isArray(coordinates) || coordinates.length < 2) {
    return 'Нет данных';
  }
  return `${coordinates[1]}, ${coordinates[0]}`;
};

function Field({
  label,
  name,
  value,
  onChange,
  type = 'text',
  textarea = false,
}) {
  return (
    <TextInputWithLabel
      type={type}
      label={label}
      textarea={textarea}
      variant="material"
      inputProps={{
        name,
        value,
        onChange,
      }}
    />
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="protocol-info-row">
      <span>{label}</span>
      <strong>{value || 'Нет данных'}</strong>
    </div>
  );
}

export default function ViolationProtocolForm(props) {
  const {
    violation = {},
    loading = false,
    onClose = () => {},
  } = props;
  const dispatch = useDispatch();

  const defaultProtocol = useMemo(() => ({
    protocol_number: `П-${String(violation?.uuid || '').slice(0, 8).toUpperCase()}`,
    address: '',
    evacuation_reason: 'Нарушение правил размещения СИМ',
    evacuation_date: moment().format('YYYY-MM-DD'),
    evacuation_time: moment().format('HH:mm'),
    storage_location: '',
    inspector_comment: '',
  }), [violation?.uuid]);

  const [protocol, setProtocol] = useState(defaultProtocol);

  const handleChange = event => {
    const { name, value } = event.target;
    setProtocol(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGenerate = () => {
    dispatch(generateViolationProtocolPdf({
      violationId: violation.uuid,
      protocol,
    }))
      .then(data => {
        if (data) {
          fileDownload(data, data.filename || 'violation_protocol.pdf');
        }
        successNotification('Протокол', 'PDF сформирован');
      })
      .catch(response => {
        errorNotification('Протокол', getResponseError(response, 'Не удалось сформировать PDF'));
      });
  };

  const photoList = violation.photo_url || [];

  return ReactDOM.createPortal(
    <div className="form-dialog violation-form violation-protocol-form">
      <div className="form-header">
        <div className="breadcrumbs">
          <span className="breadcrumbs-prev">Нарушения</span>
          <Icons width={22} height={22} customIcon icon="arrow-right" className="breadcrumbs-arrow" />
          <span className="breadcrumbs-current">Создание протокола</span>
        </div>
        <div className="close-button" onClick={onClose}>
          <Icons width={24} height={24} customIcon icon="close" />
        </div>
      </div>
      <div className="form-body">
        <form className="form-content">
          <div className="form-fields">
            <div className="form-fields-wrapper custom-scroll-theme">
              <div className="afc-body protocol-body">
                <div className="afc-main protocol-main">
                  <div className="f4">Данные нарушения</div>
                  <div className="protocol-info">
                    <InfoRow label="ID нарушения" value={violation.uuid} />
                    <InfoRow label="ID ТС" value={violation.vehicle_id} />
                    <InfoRow label="Тип ТС" value={VEHICLE_TYPE_LABELS[violation.vehicle_type] || violation.vehicle_type} />
                    <InfoRow label="Провайдер" value={violation.provider_name} />
                    <InfoRow label="Тип нарушения" value={localeFormatter('violation.type', violation.violation_type)} />
                    <InfoRow label="Статус" value={localeFormatter('violation.status', getViolationStatusDisplayValue(violation.status))} />
                    <InfoRow label="Инспектор" value={violation.inspector_login || violation.inspector_id} />
                    <InfoRow label="Координаты" value={getCoordinates(violation.location)} />
                    <InfoRow label="Зона нарушения" value={violation.zone_name} />
                    <InfoRow label="Описание" value={violation.description} />
                    <InfoRow label="Дата создания" value={dateTimeFormatter(violation.created_at)} />
                  </div>
                  <div className="f4 mt-3">Фото нарушения</div>
                  <ViolationGallery className="mt-3" photos={photoList} />
                </div>
                <div className="afc-add protocol-add">
                  <div className="f4">Поля протокола</div>
                  <Row>
                    <Col columns={6}>
                      <Field label="Номер протокола" name="protocol_number" value={protocol.protocol_number} onChange={handleChange} />
                    </Col>
                    <Col columns={6}>
                      <Field label="Дата эвакуации" name="evacuation_date" value={protocol.evacuation_date} onChange={handleChange} type="date" />
                    </Col>
                  </Row>
                  <Row>
                    <Col columns={6}>
                      <Field label="Время эвакуации" name="evacuation_time" value={protocol.evacuation_time} onChange={handleChange} type="time" />
                    </Col>
                    <Col columns={6}>
                      <Field label="Место хранения" name="storage_location" value={protocol.storage_location} onChange={handleChange} />
                    </Col>
                  </Row>
                  <Row>
                    <Col columns={12}>
                      <Field label="Адрес" name="address" value={protocol.address} onChange={handleChange} textarea />
                    </Col>
                  </Row>
                  <Row>
                    <Col columns={12}>
                      <Field label="Основание эвакуации" name="evacuation_reason" value={protocol.evacuation_reason} onChange={handleChange} textarea />
                    </Col>
                  </Row>
                  <Row>
                    <Col columns={12}>
                      <Field label="Комментарий инспектора" name="inspector_comment" value={protocol.inspector_comment} onChange={handleChange} textarea />
                    </Col>
                  </Row>
                </div>
              </div>
            </div>
          </div>
          <div className="form-footer">
            <Button onClick={handleGenerate} disabled={loading || !violation.uuid}>
              <Icons icon="download" width={16} height={16} />
              Сформировать PDF
            </Button>
            <Button variant="secondary" onClick={onClose}>Отменить</Button>
          </div>
        </form>
      </div>
    </div>,
    document.getElementById('app-content'),
  );
}
